import { useParams } from 'react-router-dom'
import { isEqual, isNumber } from 'lodash'
import {
    useFetchAnswerCategoryMismatchPredictionsQuery,
    useFetchQuestionAnswersQuery,
    useFetchTextAssignmentSubmissionsQuery,
} from '../../../../../store/apis/submission.api'
import { getReviewStatusName, SubmissionReviewStatuses } from '../../../../../constants/submission-review-statuses'
import useQueryParam from '../../../../../hooks/useQueryParam'
import {
    ASSIGNEE_IDS_PARAM,
    CONFIDENCE_LEVELS_PARAM,
    FLAGGED_FOR_MYSELF_PARAM,
    FLAGGED_PARAM,
    GROUP_BY_PARAM,
    MISMATCH_PARAM,
    REVIEW_STATUS_PARAM,
    SORT_PARAM,
    SUBMISSION_SEARCH_PARAM,
    TEST_SET_ONLY_PARAM,
} from '../../../../../constants/query-params'
import {
    DEFAULT_SUBMISSION_GROUP_BY_TYPE_FOR_ALL_QUESTIONS_PAGE,
    DEFAULT_SUBMISSION_GROUP_BY_TYPE_FOR_QUESTION_PAGE,
    SubmissionGroupByTypes,
} from '../../../../../constants/groups-by-types'
import { useFetchQuestionsQuery } from '../../../../../store/apis/question.api'
import { fetchQuestionsSelectors } from '../../../../../store/apis/utils/selectors/question.api.selectors'
import { useCallback, useDeferredValue, useEffect, useMemo } from 'react'
import {
    DEFAULT_TEXT_SUBMISSION_GROUPS_SORTING_TYPE,
    TextSubmissionGroupsSortingTypes,
} from '../../../../../constants/sorting-types'
import { useDispatch, useSelector } from 'react-redux'
import assignmentSelectors from '../../../../../store/selectors/assignment.selectors'
import { isMba, isModel, isNewTextAssignment } from '../../../../../utils/assignment.helper'
import { useVersionId } from '../../../../../utils/parseURL.helper'
import groupSelectors from '../../../../../store/selectors/group.selectors'
import { groupActions } from '../../../../../store/slices/group.slice'

const useControlGroupsCollapse = groups => {
    const isGloballyCollapsed = useSelector(groupSelectors.isGloballyCollapsed)
    const dispatch = useDispatch()

    useEffect(() => {
        if (isGloballyCollapsed) dispatch(groupActions.setCollapsedGroupsKeys(groups.map(group => group.id)))
    }, [groups, dispatch])
}

const useSubmissionGroups = ({ applyFilter = true, groupBy, questionId: questionIdProp } = {}) => {
    const assignmentType = useSelector(assignmentSelectors.type)
    const language = useSelector(assignmentSelectors.language)
    const { token, layerId: questionIdParam } = useParams()
    const groupByFromUrl = useQueryParam(GROUP_BY_PARAM)
    const groupByParam =
        groupBy || 
        groupByFromUrl ||
        (questionIdParam === 'all'
            ? DEFAULT_SUBMISSION_GROUP_BY_TYPE_FOR_ALL_QUESTIONS_PAGE
            : DEFAULT_SUBMISSION_GROUP_BY_TYPE_FOR_QUESTION_PAGE)
    const searchParam = useQueryParam(SUBMISSION_SEARCH_PARAM)
    const sortParam = useQueryParam(SORT_PARAM) || DEFAULT_TEXT_SUBMISSION_GROUPS_SORTING_TYPE
    const onlyEscalatedParam = useQueryParam(FLAGGED_PARAM) === 'true'
    const onlyEscalatedForMyselfParam = useQueryParam(FLAGGED_FOR_MYSELF_PARAM) === 'true'
    const mismatchParam = Boolean(useQueryParam(MISMATCH_PARAM))
    const reviewStatusParam = useQueryParam(REVIEW_STATUS_PARAM)
    const confidenceLevelsParam = useQueryParam(CONFIDENCE_LEVELS_PARAM)
    const assigneeIdsParam = useQueryParam(ASSIGNEE_IDS_PARAM)
    const testSetOnlyParam = useQueryParam(TEST_SET_ONLY_PARAM) === 'true'
    const questionId = questionIdProp || (['all', 'dashboard'].includes(questionIdParam) ? null : parseInt(questionIdParam))

    const versionId = useVersionId()

    const isTextAssignment = isNewTextAssignment(language)

    const { data: submissions, isFetching: isFetchingSubmissions } = useFetchTextAssignmentSubmissionsQuery(
        { token, versionId, testSetOnly: testSetOnlyParam },
        { skip: isNumber(questionId) || !isTextAssignment },
    )
    const { data: questionSubmissions, isFetching: isFetchingQuestionSubmissions } = useFetchQuestionAnswersQuery(
        {
            id: questionId,
            search: applyFilter ? searchParam : undefined,
            versionId,
            assigneeIds: applyFilter ? assigneeIdsParam?.split(',')?.map(Number) : undefined,
            testSetOnly: testSetOnlyParam,
        },
        { skip: !isNumber(questionId) || !isTextAssignment },
    )
    const { data: question, isFetching: isFetchingQuestion } = useFetchQuestionsQuery(
        { token, versionId },
        {
            skip: !isNumber(questionId) || !isTextAssignment,
            selectFromResult: result => fetchQuestionsSelectors.byId({ id: questionId }, result),
        },
    )

    const { data: answerCategoryMismatchPredictions } = useFetchAnswerCategoryMismatchPredictionsQuery(
        { questionId },
        {
            skip: !isNumber(questionId) || isMba(assignmentType) || !isTextAssignment,
        },
    )

    const filters = useMemo(
        () => ({
            searchParam,
            mismatchParam,
            reviewStatusParam,
            confidenceLevelsParam,
            onlyEscalatedParam,
            onlyEscalatedForMyselfParam,
            questionId,
        }),
        [
            searchParam,
            mismatchParam,
            reviewStatusParam,
            confidenceLevelsParam,
            onlyEscalatedParam,
            onlyEscalatedForMyselfParam,
            questionId,
        ],
    )
    const deferredFilters = useDeferredValue(filters)

    const filterSubmissions = useCallback(
        (
            submissions,
            {
                searchParam,
                mismatchParam,
                reviewStatusParam,
                confidenceLevelsParam,
                onlyEscalatedParam,
                onlyEscalatedForMyselfParam,
                questionId,
            },
        ) => {
            if (!applyFilter) return submissions

            const lowerCaseSearchParam = searchParam?.toLowerCase() || ''

            const matchesSearchParam = submission => {
                if (!searchParam || isNumber(questionId)) return true
                return submission.studentId?.toLowerCase().includes(lowerCaseSearchParam)
            }

            const matchesMismatchParam = submission => {
                if (!mismatchParam) return true
                return submission.mismatchPredicted === mismatchParam
            }

            const matchesReviewStatusParam = submission => {
                if (['any', null].includes(reviewStatusParam)) return true
                return submission.reviewStatus === Number(reviewStatusParam)
            }

            const matchesConfidenceLevelsParam = submission => {
                if (!confidenceLevelsParam) return true
                const confidenceLevels = confidenceLevelsParam.split(',').map(Number)
                return confidenceLevels.includes(submission.confidence)
            }

            const matchesOnlyEscalatedParam = submission => {
                if (!onlyEscalatedParam) return true
                return submission.escalated
            }

            const matchesOnlyEscalatedForMyselfParam = submission => {
                if (!onlyEscalatedForMyselfParam) return true

                return submission.internalNote
            }

            return submissions.filter(
                submission =>
                    matchesSearchParam(submission) &&
                    matchesMismatchParam(submission) &&
                    matchesReviewStatusParam(submission) &&
                    matchesConfidenceLevelsParam(submission) &&
                    matchesOnlyEscalatedParam(submission) &&
                    matchesOnlyEscalatedForMyselfParam(submission),
            )
        },
        [],
    )

    const addMismatchPredictedPropertyToSubmissions = useCallback((submissions, answerCategoryMismatchPredictions) => {
        return submissions
            ?.map(submission => ({
                ...submission,
                mismatchPredicted: answerCategoryMismatchPredictions?.some(
                    mismatchPrediction => mismatchPrediction.answerId === submission.answerId,
                ),
            }))
            ?.sort((a, b) => a.mismatchPredicted - b.mismatchPredicted)
    }, [])

    const sortSubmissionsInGroups = useCallback(
        groups =>
            groups.map(group => ({
                ...group,
                submissions: group.submissions.sort((a, b) => b.confidence - a.confidence),
            })),
        [],
    )

    const createGroupsForQuestion = useCallback(
        (questionSubmissions, categories, filters) => {
            let resultSubmissions = questionSubmissions || []
            resultSubmissions = addMismatchPredictedPropertyToSubmissions(
                resultSubmissions,
                answerCategoryMismatchPredictions,
            )
            resultSubmissions = filterSubmissions(resultSubmissions, filters)
            let groups = []
            if (groupByParam === SubmissionGroupByTypes.REVIEW_STATUS) {
                groups = [
                    {
                        id: SubmissionReviewStatuses.UNREVIEWED,
                        title: getReviewStatusName(SubmissionReviewStatuses.UNREVIEWED),
                        submissions: resultSubmissions?.filter(submission => !submission.approveStatus),
                    },
                    {
                        id: SubmissionReviewStatuses.REVIEWED,
                        title: getReviewStatusName(SubmissionReviewStatuses.REVIEWED),
                        submissions: resultSubmissions?.filter(submission => submission.approveStatus),
                    },
                ]
            } else if (groupByParam === SubmissionGroupByTypes.CATEGORY) {
                const categoryGroups =
                    categories?.map(category => ({
                        id: category.id,
                        title: category.name,
                        colorId: category.colorId,
                        submissions: resultSubmissions?.filter(submission => submission.categoryId === category.id),
                    })) || []

                const nullCategoryGroup = {
                    id: 'no-category',
                    title: 'No category',
                    submissions: resultSubmissions?.filter(submission => submission.categoryId === null),
                }

                groups = [...categoryGroups, nullCategoryGroup]
            }
            return sortSubmissionsInGroups(groups)
        },
        [
            addMismatchPredictedPropertyToSubmissions,
            answerCategoryMismatchPredictions,
            filterSubmissions,
            groupByParam,
            sortSubmissionsInGroups,
        ],
    )

    const createGroupsForSubmissions = useCallback(
        (submissions, filters) => {
            let resultSubmissions = submissions || []
            resultSubmissions = filterSubmissions(resultSubmissions, filters)
            if (groupByParam === SubmissionGroupByTypes.REVIEW_STATUS) {
                return Object.values(SubmissionReviewStatuses)?.map(reviewStatus => ({
                    id: reviewStatus,
                    title: getReviewStatusName(reviewStatus),
                    submissions: resultSubmissions?.filter(submission => submission.reviewStatus === reviewStatus),
                    reviewStatus,
                }))
            }
            return []
        },
        [filterSubmissions, groupByParam],
    )

    const sortGroups = useCallback((groups, sortParam) => {
        if (groups.length === 0) return groups
        switch (sortParam) {
            case TextSubmissionGroupsSortingTypes.A_Z.value:
                return groups.sort((a, b) => a.title.localeCompare(b.title))
            case TextSubmissionGroupsSortingTypes.Z_A.value:
                return groups.sort((a, b) => b.title.localeCompare(a.title))
            case TextSubmissionGroupsSortingTypes.SMALL_TO_LARGE.value:
                return groups.sort((a, b) => a.submissions?.length - b.submissions?.length)
            case TextSubmissionGroupsSortingTypes.LARGE_TO_SMALL.value:
                return groups.sort((a, b) => b.submissions?.length - a.submissions?.length)
            default:
                return groups
        }
    }, [])

    const groups = useMemo(() => {
        let resultGroups
        if (isNumber(questionId)) {
            resultGroups = createGroupsForQuestion(questionSubmissions, question?.categories, deferredFilters)
        } else {
            resultGroups = createGroupsForSubmissions(submissions, deferredFilters)
        }
        return sortGroups(resultGroups, sortParam)
    }, [
        questionId,
        questionSubmissions,
        question,
        submissions,
        createGroupsForQuestion,
        createGroupsForSubmissions,
        sortParam,
        sortGroups,
        deferredFilters,
    ])

    useControlGroupsCollapse(groups)

    return {
        groups: isTextAssignment ? groups : [],
        isFetching: isTextAssignment ? (isFetchingSubmissions || isFetchingQuestionSubmissions || isFetchingQuestion) : false,
        totalSubmissions: isTextAssignment ? (submissions?.length || questionSubmissions?.length) : 0,
        isPendingTransition: isTextAssignment ? !isEqual(filters, deferredFilters) : false,
        isAnySubmissionReviewed: isTextAssignment ? submissions?.some(submission => submission.reviewStatus === SubmissionReviewStatuses.REVIEWED) : false,
    }
}

export default useSubmissionGroups
